import Header from "./Header";
import Footer from "./Footer";
import CookieBanner from "./CookieBanner";
import SiteMessages from "./SiteMessages";
import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useSettings } from "../lib/settings";

function PageviewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Skip admin/embed/share routes — those aren't visitor-facing
    if (pathname.startsWith("/admin") || pathname.startsWith("/embed")) return;
    api.post("/track/pageview", { path: pathname }).catch(() => {});
  }, [pathname]);
  return null;
}

function MaintenanceBypassForm() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/maintenance/bypass", { password });
      window.localStorage.setItem("wp:maintenance_bypass", "1");
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Falsches Passwort");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-8 text-xs opacity-50 hover:opacity-100 transition-opacity inline-flex items-center gap-1.5"
        style={{ color: "var(--wp-olive)" }}
        data-testid="maintenance-bypass-open"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
        Zugang mit Passwort
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-sm mx-auto" data-testid="maintenance-bypass-form">
      <input
        type="password"
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Passwort"
        className="flex-1 px-4 py-2.5 rounded-full border text-sm text-center sm:text-left"
        style={{ borderColor: "var(--wp-olive)", backgroundColor: "rgba(255,255,255,0.7)" }}
        data-testid="maintenance-bypass-input"
      />
      <button
        type="submit"
        disabled={busy || !password}
        className="px-5 py-2.5 rounded-full text-sm font-medium transition-colors disabled:opacity-50"
        style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}
        data-testid="maintenance-bypass-submit"
      >
        {busy ? "…" : "Einloggen"}
      </button>
      {error && <div className="text-xs w-full text-center mt-1" style={{ color: "var(--wp-toxic, #b94a48)" }} data-testid="maintenance-bypass-error">{error}</div>}
    </form>
  );
}

function MaintenanceScreen({ message, logoUrl, appName, title, status, quote }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden"
      style={{ backgroundColor: "var(--wp-cream)" }}
      data-testid="maintenance-screen"
    >
      {/* Soft sand gradient wash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(203,188,165,0.55) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(140,138,106,0.18) 0%, transparent 60%)",
        }}
        aria-hidden="true"
      />

      {/* Decorative botanical SVG pattern – olive on cream */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none"
        viewBox="0 0 800 800"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <pattern id="leafpat" width="180" height="180" patternUnits="userSpaceOnUse" patternTransform="rotate(8)">
            <path d="M90 30 Q45 70 90 150 Q135 70 90 30 Z" fill="#5F5B3E" />
            <path d="M90 30 L90 150" stroke="#5F5B3E" strokeWidth="1.2" />
            <path d="M70 70 Q80 80 90 75 M110 70 Q100 80 90 75" stroke="#5F5B3E" strokeWidth="1" fill="none" />
          </pattern>
        </defs>
        <rect width="800" height="800" fill="url(#leafpat)" />
      </svg>

      <div className="max-w-xl text-center relative z-10">
        {logoUrl && (
          <div className="flex justify-center mb-10">
            <div
              className="rounded-full p-5 sm:p-7"
              style={{
                backgroundColor: "#FFFFFF",
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.6) inset, 0 12px 32px rgba(95,91,62,0.18), 0 4px 12px rgba(95,91,62,0.10)",
                border: "1px solid rgba(95,91,62,0.10)",
              }}
            >
              <img
                src={logoUrl}
                alt={appName}
                className="h-36 sm:h-44 w-auto mx-auto block"
                data-testid="maintenance-logo"
              />
            </div>
          </div>
        )}

        {/* Animated status pulse (replaces redundant leaf since the new logo carries the botanical mark) */}
        <div
          className="font-modern text-[0.7rem] uppercase mb-4"
          style={{ letterSpacing: "0.35em", color: "var(--wp-olive)", opacity: 0.7 }}
        >
          {appName || "Wildkräuter.lu"}
        </div>

        <h1
          className="font-display mb-6 leading-[1.05]"
          style={{
            color: "var(--wp-olive)",
            fontSize: "clamp(3rem, 8vw, 5rem)",
            fontWeight: 500,
          }}
          data-testid="maintenance-title"
        >
          {title}
        </h1>

        <p
          className="font-body text-base sm:text-lg whitespace-pre-line leading-relaxed mb-10 max-w-md mx-auto"
          style={{ color: "var(--wp-forest)", opacity: 0.85 }}
          data-testid="maintenance-message"
        >
          {message}
        </p>

        <div
          className="inline-flex items-center gap-2.5 font-modern text-xs"
          style={{ color: "var(--wp-olive)", opacity: 0.65 }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: "var(--wp-sage)", animation: "wp-pulse 1.6s ease-in-out infinite" }}
          />
          <span className="uppercase" style={{ letterSpacing: "0.15em" }} data-testid="maintenance-status">{status}</span>
        </div>

        {quote && (
          <div
            className="mt-14 pt-7 border-t inline-block w-full max-w-xs mx-auto"
            style={{ borderColor: "rgba(95,91,62,0.22)" }}
          >
            <p
              className="font-display italic text-base whitespace-pre-line"
              style={{ color: "var(--wp-olive)", opacity: 0.8 }}
              data-testid="maintenance-quote"
            >
              {quote}
            </p>
          </div>
        )}

        <MaintenanceBypassForm />
      </div>

      <style>{`
        @keyframes wp-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(0.96); }
        }
      `}</style>
    </div>
  );
}

export default function Layout() {
  const { pathname } = useLocation();
  const settings = useSettings();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  // Private / login pages: noindex
  useEffect(() => {
    if (!/^\/(profile|journal|favorites|login|register)(\/|$)/.test(pathname)) return;
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, [pathname]);

  // Hide chrome on embed pages
  const isEmbed = pathname.startsWith("/embed");
  if (isEmbed) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--wp-cream)" }}>
        <Outlet />
      </div>
    );
  }

  // Maintenance mode: allow /admin AND /embed/* to stay accessible.
  // Also allow visitors who unlocked bypass via password (localStorage flag).
  const isAdmin = pathname.startsWith("/admin");
  const hasBypass = typeof window !== "undefined" && window.localStorage.getItem("wp:maintenance_bypass") === "1";
  if (settings.maintenance_mode && !isAdmin && !isEmbed && !hasBypass) {
    return (
      <MaintenanceScreen
        message={settings.maintenance_message || "Wir sind kurz mit den Wildkräutern unterwegs – bald wieder da! 🌿"}
        logoUrl={settings.maintenance_logo_url || "/logos/logo-v1.png"}
        appName={settings.app_name || "Wildkräuter.lu"}
        title={settings.maintenance_title || "Kurz in der Natur"}
        status={settings.maintenance_status || "Schau gleich nochmal vorbei"}
        quote={settings.maintenance_quote || ""}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--wp-cream)" }}>
      <PageviewTracker />
      <ParentSiteStrip settings={settings} />
      <SiteMessages position="top" />
      <Header />
      <main className="flex-1" data-testid="main-content">
        <WelcomeBanner settings={settings} />
        <Outlet />
      </main>
      <Footer />
      <SiteMessages position="bottom" />
      <SiteMessages position="popup" />
      <CookieBanner />
    </div>
  );
}

function ParentSiteStrip({ settings }) {
  const url = settings?.parent_site_url || "https://www.adventurecouple.lu/wildkraeuterwanderung";
  const label = settings?.parent_site_label || "AdventureCouple.lu";
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full text-center font-modern uppercase transition-colors hover:opacity-90"
      style={{
        backgroundColor: "var(--wp-olive)",
        color: "var(--wp-cream)",
        fontSize: "0.65rem",
        letterSpacing: "0.25em",
        padding: "0.4rem 1rem",
      }}
      data-testid="parent-site-strip"
    >
      ← Zurück zu {label}
    </a>
  );
}

function WelcomeBanner({ settings }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!settings?.welcome_banner_enabled) return;
    // Already dismissed
    if (window.localStorage.getItem("wp:welcome_dismissed") === "1") return;
    const params = new URLSearchParams(window.location.search);
    const fromAC = params.get("from") === "ac" || params.get("utm_source") === "adventurecouple";
    const ref = (document.referrer || "").toLowerCase();
    const fromReferrer = ref.includes("adventurecouple.lu");
    if (fromAC || fromReferrer) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.welcome_banner_enabled]);

  const dismiss = () => {
    window.localStorage.setItem("wp:welcome_dismissed", "1");
    setOpen(false);
  };

  if (!open) return null;
  const title = settings?.welcome_banner_title || "Willkommen bei wildkräuter.lu";
  const text =
    settings?.welcome_banner_text ||
    "Schön, dass du da bist! Hier vertiefst du dein Wissen: Pflanzen-Datenbank, KI-Erkennung, Touren-Buchung und mehr.";
  return (
    <div
      className="relative border-b"
      style={{ backgroundColor: "var(--wp-sand)", borderColor: "var(--wp-border)" }}
      data-testid="welcome-banner"
    >
      <div className="max-w-7xl mx-auto px-6 py-5 md:py-6 flex items-start gap-4">
        <div className="flex-1">
          <div className="overline mb-1" style={{ color: "var(--wp-olive)" }}>Schön, dass du da bist</div>
          <h2 className="font-display text-2xl md:text-3xl mb-2" style={{ color: "var(--wp-olive)" }}>
            {title}
          </h2>
          <p className="font-body text-sm md:text-base leading-relaxed max-w-2xl" style={{ color: "var(--wp-forest)" }}>
            {text}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href="/plants" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium" style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }} data-testid="welcome-cta-plants">
              Pflanzen-Datenbank →
            </a>
            <a href="/tours" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium border" style={{ borderColor: "var(--wp-olive)", color: "var(--wp-olive)" }} data-testid="welcome-cta-tours">
              Tour buchen →
            </a>
            <a href="/identify" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium border" style={{ borderColor: "var(--wp-olive)", color: "var(--wp-olive)" }} data-testid="welcome-cta-identify">
              Pflanze erkennen →
            </a>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-2 rounded-full hover:bg-white/40 transition-colors shrink-0"
          aria-label="Banner schließen"
          data-testid="welcome-banner-close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>
    </div>
  );
}
